import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { encryptData, decryptData } from "../../utils/encryption";

function LoginModal({ onClose, onLoginSuccess }) {
  const [view, setView] = useState("login"); // 'login', 'signup', 'forgot', 'otp'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    otp: "",
    newPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // For inline error messages
  const navigate = useNavigate();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Universal error handler
  const handleError = (message) => {
    setError(message);
    setLoading(false);
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const encryptedPayload = encryptData({
        email: formData.email,
        password: formData.password,
      });

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/login`, {
        encryptedData: encryptedPayload,
      });

      const decryptedResponse = decryptData(response.data);
      console.log("Decrypted Login Response:", decryptedResponse);

      const { token, user } = decryptedResponse;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      onLoginSuccess();
      console.log("Navigating to /dashboard...");
      navigate("/dashboard");
      onClose();
    } catch (error) {
      console.error("Login Error:", error);

      // Handle 404 error specifically
      if (error.response && error.response.status === 404) {
        console.log("User not found. Switching to signup view.");
        setView("signup"); // Switch to signup view
        setError(null); // Clear any existing error message
      } else if (error.response && error.response.data) {
        try {
          const decryptedError = decryptData(error.response.data);
          setError(decryptedError.message || "Login failed.");
        } catch (decryptionError) {
          console.error("Error decrypting server error response:", decryptionError);
          setError("An error occurred. Please try again.");
        }
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };


  // Handle signup submission
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const encryptedPayload = encryptData({
        firstName: formData.firstname,
        lastName: formData.lastname,
        email: formData.email,
        password: formData.password,
      });

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/register`, {
        encryptedData: encryptedPayload,
      });

      const decryptedResponse = decryptData(response.data); // Decrypt success message
      alert(decryptedResponse.message || "Account created successfully. Please log in.");
      setView("login");
    } catch (error) {
      console.error("Signup Error:", error);

      if (error.response) {
        const decryptedError = decryptData(error.response.data); // Decrypt error response
        if (decryptedError.message === "User already exists") {
          alert("User already exists. Please log in.");
          setView("login"); // Switch to login modal
        } else {
          handleError(decryptedError.message || "Error creating account. Please try again.");
        }
      } else {
        handleError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Other methods remain the same, but apply `decryptData` to backend responses
  // Handle forgot password submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const encryptedPayload = encryptData({ email: formData.email });

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/forgot-password`, {
        encryptedData: encryptedPayload,
      });

      const decryptedResponse = decryptData(response.data); // Decrypt success message
      alert(decryptedResponse.message || "OTP sent to your email. Please enter it below.");
      setView("otp"); // Switch to OTP entry view
    } catch (error) {
      console.error("Forgot Password Error:", error);
      if (error.response) {
        const decryptedError = decryptData(error.response.data); // Decrypt error response
        handleError(decryptedError.message || "Error sending OTP. Please try again.");
      } else {
        handleError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification and password reset
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const encryptedPayload = encryptData({
        email: formData.email,
        otp: formData.otp,
        newPassword: formData.newPassword,
      });

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/verify-otp`, {
        encryptedData: encryptedPayload,
      });

      const decryptedResponse = decryptData(response.data); // Decrypt success message
      alert(decryptedResponse.message || "Password reset successfully. Please log in.");
      setView("login");
    } catch (error) {
      console.error("Verify OTP Error:", error);
      if (error.response) {
        const decryptedError = decryptData(error.response.data); // Decrypt error response
        handleError(decryptedError.message || "Invalid OTP or expired. Please try again.");
      } else {
        handleError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="popup-container">
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>

        {error && <div className="error-message">{error}</div>}


        {/* Login View */}
        {view === "login" && (
          <div className="login-form">
            <h2>Login</h2>
            <form onSubmit={handleLogin}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="input-field"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
              <div className="popup-footer">
                <span onClick={() => { setView("forgot"); setError(null); }}>Forgot Password?</span> |{" "}
                <span onClick={() => { setView("signup"); setError(null); }}>Create an Account</span>
              </div>
            </form>
          </div>
        )}

        {/* Signup View */}
        {view === "signup" && (
          <div className="signup-form">
            <h2>Create an Account</h2>
            <form onSubmit={handleSignup}>
              <input
                type="text"
                name="firstname"
                placeholder="First Name"
                className="input-field"
                value={formData.firstname}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="lastname"
                placeholder="Last Name"
                className="input-field"
                value={formData.lastname}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="input-field"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Signing up..." : "Sign Up"}
              </button>
              <div className="popup-footer">
                <span onClick={() => { setView("login"); setError(null); }}>Already have an account? Login</span>
              </div>
            </form>
          </div>
        )}

        {/* Forgot Password View */}
        {view === "forgot" && (
          <div className="forgot-form">
            <h2>Forgot Password</h2>
            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Sending..." : "Reset Password"}
              </button>
              <div className="popup-footer">
                <span onClick={() => setView("login")}>Back to Login</span>
              </div>
            </form>
          </div>
        )}

        {/* OTP Verification View */}
        {view === "otp" && (
          <div className="otp-form">
            <h2>Verify OTP</h2>
            <form onSubmit={handleVerifyOTP}>
              <input
                type="text"
                name="otp"
                placeholder="Enter OTP"
                className="input-field"
                value={formData.otp}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="newPassword"
                placeholder="Enter New Password"
                className="input-field"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Verifying..." : "Reset Password"}
              </button>
              <div className="popup-footer">
                <span onClick={() => setView("forgot")}>Back to Forgot Password</span>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginModal;
